import { usesHomeImprovementAgreement } from '../../common/helpers/esrContractCompany'
import type { Customer } from '../../common/schema/schema'
import { getApiBaseUrl } from './sendContractByEmail'

// Guided Finish uses the durable, atomic delivery receipt before sending.
export const AUTO_EMAIL_SIGNED_CONTRACT_ENABLED = true

export const AUTO_EMAIL_SIGNED_CONTRACT_ERROR =
  "We couldn't email the signed agreement. Please contact support."

export type PrimaryContractSignatureField =
  | 'home_improvement_sign_owner1'
  | 'security_contract_sign_customer'

export function primaryContractSignatureField(
  companyShortName: string | null | undefined,
): PrimaryContractSignatureField | null {
  // MSE, CE and ESR (13376550851) share the M+S agreement and its fields.
  if (usesHomeImprovementAgreement(companyShortName)) {
    return 'home_improvement_sign_owner1'
  }

  if (
    companyShortName === 'FE' ||
    companyShortName === 'HC' ||
    companyShortName === 'Wildcat' ||
    companyShortName === 'OTHER_CASH' ||
    companyShortName === 'OTHER_FIN'
  ) {
    return 'security_contract_sign_customer'
  }

  return null
}

export function pdfBytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 0x8000
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const end = Math.min(offset + chunkSize, bytes.length)
    const charCodes: number[] = []
    for (let index = offset; index < end; index += 1) {
      charCodes.push(bytes[index]!)
    }
    binary += String.fromCharCode(...charCodes)
  }
  return btoa(binary)
}

export interface SignedContractEmailRequest {
  customerEmail: string
  customerName: string
  fileName: string
  pdfBase64: string
}

export async function postSignedContractEmail(
  request: SignedContractEmailRequest,
  signal?: AbortSignal,
  idToken?: string,
): Promise<void> {
  const response = await fetch(`${getApiBaseUrl()}/send-contract`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: 'Bearer ' + idToken } : {}),
    },
    body: JSON.stringify(request),
    signal,
  })
  if (!response.ok) throw new Error(AUTO_EMAIL_SIGNED_CONTRACT_ERROR)
  const body = (await response.json().catch(() => null)) as {
    ok?: boolean
  } | null
  if (body?.ok !== true) throw new Error(AUTO_EMAIL_SIGNED_CONTRACT_ERROR)
}

export interface AutoEmailSignedContractInput {
  enabled: boolean
  signatureWasJustSaved: boolean
  readOnly: boolean
  customerID: string
  customerName: string
  customerEmail: string
  contractCompanyShortName: string | null | undefined
  signatureField: keyof Customer | string
  signatureValue: string
  renderedSignatureValue: string
  sentAt: number
}

export interface GeneratedContractPdf {
  bytes: Uint8Array
  fileName: string
}

export interface AutoEmailSignedContractDependencies {
  isCancelled(): boolean
  waitForRenderedSignature(): Promise<void>
  capturePdf(customerName: string): Promise<GeneratedContractPdf | null>
  send(request: SignedContractEmailRequest): Promise<void>
  markSentAt(customerID: string, timestamp: number): void | Promise<void>
  now(): number
}

export type AutoEmailSignedContractResult = 'sent' | 'skipped'

const inFlight = new Set<string>()
const sentThisRuntime = new Set<string>()

export async function autoEmailSignedContract(
  input: AutoEmailSignedContractInput,
  dependencies: AutoEmailSignedContractDependencies,
): Promise<AutoEmailSignedContractResult> {
  if (
    !input.enabled ||
    !input.signatureWasJustSaved ||
    input.readOnly ||
    dependencies.isCancelled()
  ) {
    return 'skipped'
  }

  const expectedField = primaryContractSignatureField(
    input.contractCompanyShortName,
  )
  const customerEmail = input.customerEmail.trim()
  const customerName = input.customerName.trim()
  const customerID = input.customerID.trim()

  if (
    !expectedField ||
    input.signatureField !== expectedField ||
    !input.signatureValue.trim() ||
    input.renderedSignatureValue !== input.signatureValue ||
    !customerEmail ||
    !customerName ||
    !customerID ||
    input.sentAt > 0
  ) {
    return 'skipped'
  }

  const key = `${customerID}:${expectedField}`
  if (inFlight.has(key) || sentThisRuntime.has(key)) return 'skipped'

  inFlight.add(key)
  try {
    await dependencies.waitForRenderedSignature()
    if (dependencies.isCancelled()) return 'skipped'
    const pdf = await dependencies.capturePdf(customerName)
    if (dependencies.isCancelled()) return 'skipped'
    if (!pdf || pdf.bytes.length === 0) {
      throw new Error(AUTO_EMAIL_SIGNED_CONTRACT_ERROR)
    }

    if (dependencies.isCancelled()) return 'skipped'
    await dependencies.send({
      customerEmail,
      customerName,
      fileName: pdf.fileName,
      pdfBase64: pdfBytesToBase64(pdf.bytes),
    })

    sentThisRuntime.add(key)
    try {
      await dependencies.markSentAt(customerID, dependencies.now())
    } catch {
      // Email already succeeded. Runtime guard prevents an immediate duplicate;
      // cross-reload/device deduplication requires the server-side CAS guard.
    }
    return 'sent'
  } finally {
    inFlight.delete(key)
  }
}
